// import node module libraries
import React, {Fragment, useEffect, useMemo, useState} from 'react';
import {FormSelect} from 'components/elements/form-select/FormSelect';
import {Link} from 'react-router-dom';
import {Col, Form, Row, Table} from 'react-bootstrap';
import {useFilters, useGlobalFilter, usePagination, useTable} from 'react-table';

// import custom components
import GlobalFilter from 'components/elements/advance-table/GlobalFilter';
import Pagination from 'components/elements/advance-table/Pagination';

// import utility file
import DepenserecetteService from '../../services/DepenserecetteService';
import PersonnelService from '../../services/PersonnelService';


const DepenserecettesListItems = () => {
    const [originalDepenserecettes, setOriginalDepenserecettes] = useState([]);
    const [depenserecettes, setDepenserecettes] = useState([]);
    const [personnel, setPersonnel] = useState([]); // Add this line
    const [personnelOptions, setPersonnelOptions] = useState([]);

    useEffect(() => {
        const fetchPersonnelOptions = async () => {
            try {
                const personnels = await PersonnelService.getPersonnelOptions();
                setPersonnelOptions(personnels);
            } catch (error) {
                console.error('Error fetching class options', error);
            }
        };

        fetchPersonnelOptions();
    }, []);

    const PERSONNEL_CHOICES = personnelOptions;

    useEffect(() => {
        Promise.all([
            DepenserecetteService.getAllDepenserecettes(),
            PersonnelService.getAllActivePersonnels()
        ]).then(([depenserecettesData, personnelData]) => {
            setOriginalDepenserecettes(depenserecettesData);
            setDepenserecettes(depenserecettesData);
            setPersonnel(personnelData);
        });
    }, []);

    const CustomToggle = React.forwardRef(({children, onClick}, ref) => (
        <Link
            to=""
            ref={ref}
            onClick={(e) => {
                e.preventDefault();
                onClick(e);
            }}
            className="btn-icon btn btn-ghost btn-sm rounded-circle"
        >
            {children}
        </Link>
    ));

    const formatValue = (value, columnId) => {
        if (typeof value === 'string') {
            if (
                columnId === 'type' || columnId === 'fournisseur' && value) {
                return (
                    value && value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
                );
            } else if (columnId === 'montant') {
                return new Intl.NumberFormat('fr-FR', {style: 'currency', currency: 'XAF'}).format(value);
            }
        } else {
            return value;
        }
    };

    const formatStatutDepenserecette = (value) => {
        // Remplacez les underscores par des espaces et mettez la première lettre en majuscule
        return value
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase());
    };

    const getPersonnelById = (personnelId) => {
        return personnel.find((item) => item.id === personnelId) ?? {};
    };

    const columns = useMemo(
        () => [
            {accessor: 'id', Header: 'ID', show: false},
            {
                accessor: 'type',
                Header: 'Type',
                Cell: ({value, row}) => {
                    return (
                        <div className="d-flex align-items-center">
                            {value ? (
                                <Link to={`/finances/bilan/${row.original.id}`}>
                                    <h5 className="mb-0 ms-2">{formatValue(value, 'type')}</h5>
                                </Link>
                            ) : null}
                        </div>
                    );
                }
            },
            {
                accessor: 'date',
                Header: 'Date',
                Cell: ({value}) => <div>{formatValue(value, 'date')}</div>
            },
            {
                accessor: 'montant',
                Header: 'Montant',
                Cell: ({value}) => <div>{formatValue(value, 'montant')}</div>
            },
            {
                accessor: 'fournisseur',
                Header: 'Fournisseur',
                Cell: ({value}) => <div>{formatValue(value, 'fournisseur')}</div>
            },
        ],
        []
    );

    const data = useMemo(() => {
        return depenserecettes.map((depenserecette) => ({
            ...depenserecette,
            personnel: getPersonnelById(depenserecette.personnel),
        }));
    }, [depenserecettes]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        nextPage,
        previousPage,
        state,
        gotoPage,
        pageCount,
        prepareRow,
        setGlobalFilter
    } = useTable(
        {
            columns,
            data,
            initialState: {
                pageSize: 10,
                hiddenColumns: columns.map((column) => {
                    if (column.show === false) return column.accessor || column.id;
                    else return false;
                })
            }
        },
        useFilters,
        useGlobalFilter,
        usePagination
    );

    const {pageIndex, globalFilter} = state;

    const getFilterTerm = (event) => {
        let filterTerm = event.target.value;
        if (filterTerm !== '') {
            const newDepenserecettesList = originalDepenserecettes.filter((depenserecette) => {
                return depenserecette.personnel === Number(filterTerm);
            });
            setDepenserecettes(newDepenserecettesList);
        } else {
            setDepenserecettes(originalDepenserecettes);
        }
    };

    return (
        <Fragment>
            <div className="overflow-hidden p-3">
                <Row className="justify-content-md-between mb-4 mb-xl-0 ">
                    <Col xxl={3} lg={4} md={6} xs={12}>
                        <GlobalFilter
                            filter={globalFilter}
                            setFilter={setGlobalFilter}
                            placeholder="Rechercher un Bilan"
                        />
                    </Col>
                    <Col xxl={3} lg={4} md={6} xs={12}>
                        {/* records filtering options */}
                        <Form.Control
                            as={FormSelect}
                            placeholder="Filtrer personnel"
                            options={PERSONNEL_CHOICES}
                            onChange={getFilterTerm}
                        />
                    </Col>
                </Row>
            </div>

            <Table
                hover
                responsive
                {...getTableProps()}
                className="table text-nowrap table-centered"
            >
                <thead className="table-light">
                {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                            <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                {page.map((row) => {
                    prepareRow(row);
                    return (
                        <tr {...row.getRowProps()}>
                            {row.cells.map((cell) => {
                                return (
                                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                                );
                            })}
                        </tr>
                    );
                })}
                </tbody>
            </Table>

            <Pagination
                previousPage={previousPage}
                pageCount={pageCount}
                pageIndex={pageIndex}
                gotoPage={gotoPage}
                nextPage={nextPage}
            />
        </Fragment>
    );
};

export default DepenserecettesListItems;
